import React, { useContext, useEffect, useRef, useState } from 'react';
import { ConfigurationContext } from '../DataContexts/ConfigurationContext';
import ConfigSection from './ConfigSection';
import ModelCard from './ModelCard';
import {
  priceSheetSmallUrl,
  validatePriceSheetFile,
  PRICE_SHEET_WIDTH,
  PRICE_SHEET_HEIGHT,
} from './priceSheet';
import {
  formatEuro,
  euroToCents,
  centsToEuroInput,
  parseIntOrNull,
  isPayconiqEntry,
} from './configFormat';

// DJB-178: the backend only accepts a mode with 3–4 payconiq (mobile-pay)
// entries — the cause of the earlier opaque 400s on create.
export const MIN_PAYCONIQ_SLOTS = 3;
export const MAX_PAYCONIQ_SLOTS = 4;

const emptyTierRow = () => ({ price: '', credits: '', payconiq: false });

// DJB-218: the backend checks the PNG file signature; mirror that cheaply on
// the client (MIME type or .png extension) so a wrong pick fails before the
// upload round-trip.
export const isPngFile = (file) =>
  Boolean(file) &&
  (file.type === 'image/png' || (file.type === '' && /\.png$/i.test(file.name || '')));

// Object-URL preview of a picked File; jsdom has no URL.createObjectURL, so
// the preview is simply skipped where the API is missing.
const useFilePreview = (file) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (!file || typeof URL.createObjectURL !== 'function') {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => {
      if (typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
    };
  }, [file]);
  return previewUrl;
};

// DJB-218: price-sheet PNG of an existing payment mode. Its URL follows from
// the mode id (S3 bucket root, {id}-small.png — the sheet the jukebox shows),
// so whether a sheet exists is learned from the image itself: it loads or it
// 404s. `version` comes from the context: a timestamp after an upload in this
// session (cache buster), undefined when not yet known. A sheet can only be
// replaced, never removed — a mode must always have one; the objects go with
// the mode itself (backend cleanup on delete).
const PriceSheetPanel = ({ mode, version, onUpload }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // 'unknown' | 'present' | 'missing'
  const [status, setStatus] = useState(() => statusFromVersion(version));
  // an upload/removal in this session changes `version` → re-derive the status
  const [seenVersion, setSeenVersion] = useState(version);
  if (seenVersion !== version) {
    setSeenVersion(version);
    setStatus(statusFromVersion(version));
  }
  const fileInputRef = useRef(null);
  const inputId = `price-sheet-file-${mode.id}`;

  const sheetUrl = priceSheetSmallUrl(mode.id, version);
  const hasSheet = status === 'present';

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const fileError = await validatePriceSheetFile(file, isPngFile);
    if (fileError) {
      setError(fileError);
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onUpload(mode, file);
    } catch (uploadError) {
      // notification shown by the context
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="config-price-sheet">
      <p className="config-mode-card-subtitle">Price sheet</p>
      {status === 'missing' ? (
        <p className="config-muted">No price sheet uploaded.</p>
      ) : (
        <a
          className="config-price-sheet-link"
          href={sheetUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            className="config-price-sheet-image"
            src={sheetUrl}
            alt={`Price sheet for ${mode.name}`}
            onLoad={() => setStatus('present')}
            onError={() => setStatus('missing')}
          />
        </a>
      )}
      <div className="config-price-sheet-actions">
        <input
          ref={fileInputRef}
          id={inputId}
          className="config-file-input"
          type="file"
          accept="image/png,.png"
          aria-label={`${hasSheet ? 'Replace' : 'Upload'} price sheet for ${mode.name}`}
          onChange={handleFileChange}
          disabled={busy}
        />
        <button
          type="button"
          className="config-ghost-btn"
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        >
          {busy ? 'Working…' : hasSheet ? 'Replace price sheet' : 'Upload price sheet'}
        </button>
      </div>
      {error && (
        <p className="config-form-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

const isPriceSheetError = (message) =>
  Boolean(message) && message.startsWith('The price sheet must be');

const statusFromVersion = (version) => (version ? 'present' : 'unknown');

// DJB-177: collapsed one-liner — the price loop range plus the payconiq slots
const modeSummary = (mode) => {
  const entries = mode.entries || [];
  if (entries.length === 0) return 'No tiers';
  const prices = entries.map((entry) => Number(entry.pricepoint)).filter(Number.isFinite);
  const loop = `${formatEuro(Math.min(...prices))} → ${formatEuro(Math.max(...prices))}`;
  const slotPrices = entries.filter(isPayconiqEntry).map((entry) => formatEuro(entry.pricepoint));
  const slots =
    slotPrices.length === 0
      ? '0 mobile-pay slots'
      : `${slotPrices.length} mobile-pay slot${slotPrices.length === 1 ? '' : 's'} at ${slotPrices.join(', ')}`;
  return `${loop} — ${slots}`;
};

// DJB-200: tiers sorted by price with the cumulative credit amount up to and
// including each tier — the same running total the create form simulates.
const tierRowsWithTotals = (entries) => {
  let runningTotal = 0;
  return [...entries]
    .sort((a, b) => Number(a.pricepoint) - Number(b.pricepoint))
    .map((entry) => {
      runningTotal += Number(entry.credits) || 0;
      return { entry, totalCredits: runningTotal };
    });
};

const PaymentModeCard = ({
  mode,
  priceSheetVersion,
  onDelete,
  onUploadPriceSheet,
}) => {
  const rows = tierRowsWithTotals(mode.entries || []);

  return (
    <ModelCard
      title={mode.name}
      summary={modeSummary(mode)}
      actions={
        <button
          type="button"
          className="config-danger-btn"
          onClick={() => onDelete(mode)}
          aria-label={`Delete payment mode ${mode.name}`}
        >
          Delete
        </button>
      }
    >
      {rows.length === 0 ? (
        <p className="config-muted">No tiers</p>
      ) : (
        <table className="config-tier-table">
          <thead>
            <tr>
              <th scope="col">Price Tier</th>
              <th scope="col">Credits</th>
              <th scope="col">Mobile Pay</th>
              <th scope="col">Total Credits</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ entry, totalCredits }, index) => (
              <tr key={index}>
                <td>{formatEuro(entry.pricepoint)}</td>
                <td>{entry.credits}</td>
                <td>{isPayconiqEntry(entry) ? 'Yes' : 'No'}</td>
                <td>{totalCredits}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <PriceSheetPanel
        mode={mode}
        version={priceSheetVersion}
        onUpload={onUploadPriceSheet}
      />
    </ModelCard>
  );
};

// DJB-161: overview of all payment modes + create-new-mode form. Editing or
// renaming an existing mode is intentionally unsupported (no backend PUT) —
// the tiers of a mode are fixed at creation.
const PaymentModelSection = () => {
  const {
    paymentModes,
    paymentModesLoading,
    priceSheetVersions,
    createPaymentMode,
    deletePaymentMode,
    uploadPriceSheet,
  } = useContext(ConfigurationContext);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  // DJB-218: optional price-sheet PNG, uploaded right after the mode is created
  const [priceSheetFile, setPriceSheetFile] = useState(null);
  const priceSheetPreview = useFilePreview(priceSheetFile);
  const [tierCount, setTierCount] = useState('');
  const [stepPrice, setStepPrice] = useState('');
  const [tierCredits, setTierCredits] = useState('');
  const [rows, setRows] = useState([]);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  // DJB-200: simulated running total — the credit amount a customer has
  // collected up to and including each tier. Display only, never editable.
  const rowTotals = rows.reduce((totals, row) => {
    const previous = totals.length > 0 ? totals[totals.length - 1] : 0;
    totals.push(previous + (parseIntOrNull(row.credits) ?? 0));
    return totals;
  }, []);

  const payconiqCount = rows.filter((row) => row.payconiq).length;
  const payconiqCountOk =
    payconiqCount >= MIN_PAYCONIQ_SLOTS && payconiqCount <= MAX_PAYCONIQ_SLOTS;

  const resetForm = () => {
    setName('');
    setPriceSheetFile(null);
    setTierCount('');
    setStepPrice('');
    setTierCredits('');
    setRows([]);
    setFormError(null);
  };

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  // DJB-178: tier i gets a CUMULATIVE price (i × step) but the SAME credit
  // amount on every tier — the prefill only seeds the rows, every value stays
  // individually editable afterwards.
  const handlePrefill = () => {
    const count = parseIntOrNull(tierCount);
    const stepCents = euroToCents(stepPrice);
    const credits = parseIntOrNull(tierCredits);
    if (
      count == null ||
      count < 1 ||
      stepCents == null ||
      stepCents <= 0 ||
      credits == null ||
      credits < 1
    ) {
      setFormError('Prefill needs a number of tiers, a price above €0.00 and at least 1 credit.');
      return;
    }
    setFormError(null);
    setRows(
      Array.from({ length: count }, (_, index) => ({
        price: centsToEuroInput((index + 1) * stepCents),
        credits: String(credits),
        payconiq: false,
      }))
    );
  };

  const handlePriceSheetChange = async (event) => {
    const input = event.target;
    const file = input.files?.[0] || null;
    const fileError = file ? await validatePriceSheetFile(file, isPngFile) : null;
    if (fileError) {
      input.value = '';
      setPriceSheetFile(null);
      setFormError(fileError);
      return;
    }
    setFormError((current) => (isPriceSheetError(current) ? null : current));
    setPriceSheetFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!name.trim()) {
      setFormError('Give the payment mode a name.');
      return;
    }
    if (priceSheetFile) {
      const fileError = await validatePriceSheetFile(priceSheetFile, isPngFile);
      if (fileError) {
        setFormError(fileError);
        return;
      }
    }
    const entries = [];
    for (const row of rows) {
      if (row.price === '' && row.credits === '' && !row.payconiq) continue;
      const pricepoint = euroToCents(row.price);
      const credits = parseIntOrNull(row.credits);
      if (pricepoint == null || pricepoint <= 0 || credits == null || credits <= 0) {
        setFormError('Every tier needs a price above €0.00 and at least 1 credit.');
        return;
      }
      entries.push({ pricepoint, credits, payconiqEntry: row.payconiq });
    }
    if (entries.length === 0) {
      setFormError('Prefill or add at least one tier.');
      return;
    }
    const slotCount = entries.filter((entry) => entry.payconiqEntry).length;
    if (slotCount < MIN_PAYCONIQ_SLOTS || slotCount > MAX_PAYCONIQ_SLOTS) {
      setFormError(
        `Mark between ${MIN_PAYCONIQ_SLOTS} and ${MAX_PAYCONIQ_SLOTS} tiers as mobile-pay slots.`
      );
      return;
    }
    setFormError(null);
    setSaving(true);
    let created;
    try {
      created = await createPaymentMode({ name: name.trim(), entries });
    } catch (error) {
      // notification shown by the context; keep the form open for a retry
      setSaving(false);
      return;
    }
    // DJB-218: the sheet needs the new mode's id, so it goes up in a second
    // request. The mode exists either way — close the form (re-submitting
    // would create a duplicate) and let the card's upload control retry.
    if (priceSheetFile && created?.id) {
      try {
        await uploadPriceSheet(created.id, priceSheetFile);
      } catch (error) {
        // notification shown by the context
      }
    }
    resetForm();
    setShowForm(false);
    setSaving(false);
  };

  const handleUploadPriceSheet = async (mode, file) => {
    await uploadPriceSheet(mode.id, file);
  };

  const handleDelete = async (mode) => {
    const confirmed = window.confirm(`Are you sure you want to delete payment mode "${mode.name}"?`);
    if (!confirmed) return;
    try {
      await deletePaymentMode(mode.id);
    } catch (error) {
      // notification shown by the context
    }
  };

  return (
    <ConfigSection
      anchorId="section-paymentmodel"
      title="Paymentmodel"
      actions={
        !showForm && (
          <button type="button" className="config-primary-btn" onClick={() => setShowForm(true)}>
            New payment mode
          </button>
        )
      }
    >
      <p className="config-section-intro">
        Coin/credit tiers and Payconiq mobile-pay slots per payment mode. A mode is assigned to a
        machine; its tiers are fixed at creation (create a new mode to change them) and{' '}
        {MIN_PAYCONIQ_SLOTS}–{MAX_PAYCONIQ_SLOTS} of them must be mobile-pay slots. Each mode can
        carry a price-sheet PNG of exactly {PRICE_SHEET_WIDTH}×{PRICE_SHEET_HEIGHT} pixels, the
        box the jukebox shows it in.
      </p>

      {showForm && (
        <form className="config-form" onSubmit={handleSubmit}>
          <div className="config-form-field">
            <label htmlFor="payment-mode-name">Name</label>
            <input
              id="payment-mode-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. 50c-1 / 1eur-3"
            />
          </div>
          <div className="config-form-field config-price-sheet-field">
            <label htmlFor="payment-mode-price-sheet">
              Price sheet (PNG {PRICE_SHEET_WIDTH}×{PRICE_SHEET_HEIGHT}, optional)
            </label>
            <input
              id="payment-mode-price-sheet"
              type="file"
              accept="image/png,.png"
              onChange={handlePriceSheetChange}
            />
            {priceSheetFile && (
              <div className="config-price-sheet-picked">
                {priceSheetPreview && (
                  <img
                    className="config-price-sheet-image config-price-sheet-preview"
                    src={priceSheetPreview}
                    alt={`Preview of ${priceSheetFile.name}`}
                  />
                )}
                <span className="config-muted">{priceSheetFile.name}</span>
                <button
                  type="button"
                  className="config-icon-btn"
                  aria-label="Clear price sheet"
                  onClick={() => {
                    setPriceSheetFile(null);
                    const input = document.getElementById('payment-mode-price-sheet');
                    if (input) input.value = '';
                  }}
                >
                  ×
                </button>
              </div>
            )}
          </div>
          <div className="config-form-row config-prefill-row">
            <div className="config-form-field">
              <label htmlFor="payment-mode-tier-count">Tiers</label>
              <input
                id="payment-mode-tier-count"
                type="number"
                min="1"
                step="1"
                value={tierCount}
                onChange={(e) => setTierCount(e.target.value)}
              />
            </div>
            <div className="config-form-field">
              <label htmlFor="payment-mode-step-price">Price step (€)</label>
              <input
                id="payment-mode-step-price"
                type="number"
                min="0"
                step="0.01"
                value={stepPrice}
                onChange={(e) => setStepPrice(e.target.value)}
              />
            </div>
            <div className="config-form-field">
              <label htmlFor="payment-mode-tier-credits">Credits per tier</label>
              <input
                id="payment-mode-tier-credits"
                type="number"
                min="1"
                step="1"
                value={tierCredits}
                onChange={(e) => setTierCredits(e.target.value)}
              />
            </div>
            <button type="button" className="config-ghost-btn" onClick={handlePrefill}>
              Prefill tiers
            </button>
          </div>
          <p className="config-muted">
            Prefill fills tier i at i × the price step, all with the same credit amount; price and
            credits stay editable. Tick {MIN_PAYCONIQ_SLOTS}–{MAX_PAYCONIQ_SLOTS} tiers as
            mobile-pay (Payconiq) slots. Total Credits shows the running total up to and including
            each tier.
          </p>
          <div className="config-entry-editor">
            {rows.length === 0 && (
              <p className="config-entry-editor-empty">No tiers yet — prefill or add one.</p>
            )}
            {rows.length > 0 && (
              <table className="config-tier-table config-tier-edit-table">
                <thead>
                  <tr>
                    <th scope="col">Price Tier (€)</th>
                    <th scope="col">Credits</th>
                    <th scope="col">Mobile Pay</th>
                    <th scope="col">Total Credits</th>
                    <th scope="col" aria-label="Remove tier" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Price (€)"
                          aria-label={`Tier ${index + 1} price in euro`}
                          value={row.price}
                          onChange={(e) => updateRow(index, 'price', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          placeholder="Credits"
                          aria-label={`Tier ${index + 1} credits`}
                          value={row.credits}
                          onChange={(e) => updateRow(index, 'credits', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={row.payconiq}
                          onChange={(e) => updateRow(index, 'payconiq', e.target.checked)}
                          aria-label={`Tier ${index + 1} mobile-pay slot`}
                        />
                      </td>
                      <td
                        className="config-total-credits-cell"
                        aria-label={`Tier ${index + 1} total credits`}
                      >
                        {rowTotals[index]}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="config-icon-btn"
                          aria-label={`Remove tier ${index + 1}`}
                          onClick={() => setRows((prev) => prev.filter((_, i) => i !== index))}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <button
              type="button"
              className="config-ghost-btn"
              onClick={() => setRows((prev) => [...prev, emptyTierRow()])}
            >
              Add tier
            </button>
          </div>
          <p className={payconiqCountOk ? 'config-muted' : 'config-slot-count-warning'}>
            Mobile-pay slots selected: {payconiqCount} (need {MIN_PAYCONIQ_SLOTS}–
            {MAX_PAYCONIQ_SLOTS})
          </p>
          {formError && (
            <p className="config-form-error" role="alert">
              {formError}
            </p>
          )}
          <div className="config-form-actions">
            <button
              type="button"
              className="config-ghost-btn"
              onClick={() => {
                resetForm();
                setShowForm(false);
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="config-primary-btn"
              disabled={saving || !payconiqCountOk}
            >
              {saving ? 'Creating…' : 'Create payment mode'}
            </button>
          </div>
        </form>
      )}

      {paymentModesLoading ? (
        <p className="config-muted">Loading payment modes…</p>
      ) : paymentModes.length === 0 ? (
        <p className="config-muted">No payment modes yet.</p>
      ) : (
        <div className="config-mode-cards">
          {paymentModes.map((mode) => (
            <PaymentModeCard
              key={mode.id}
              mode={mode}
              priceSheetVersion={priceSheetVersions?.[mode.id]}
              onDelete={handleDelete}
              onUploadPriceSheet={handleUploadPriceSheet}
            />
          ))}
        </div>
      )}
    </ConfigSection>
  );
};

export default PaymentModelSection;
